import { dashboardApiBaseUrl, useMocks } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import { MOCK_PLATFORM_METRICS, MOCK_PLATFORM_OPERATIONS } from './mockPlatformData';
import type { PlatformMetricDefinition, PlatformOperationsResponse } from './types';

/**
 * The frontend seam to the Super Admin Operations and Risk Center. Reserved to a caller holding `platform.dashboard.read` (SuperAdmin): the server, not this
 * gateway or any client-side check, enforces that, and a caller without it gets a real 403 (ApiError) which the page shows as its own restricted state.
 */
export interface PlatformOperationsGateway {
  getOperations(signal?: AbortSignal): Promise<PlatformOperationsResponse>;
  getMetrics(signal?: AbortSignal): Promise<PlatformMetricDefinition[]>;
}

export class HttpPlatformOperationsGateway implements PlatformOperationsGateway {
  private readonly request = createJsonClient(dashboardApiBaseUrl);

  getOperations(signal?: AbortSignal): Promise<PlatformOperationsResponse> {
    return this.request('/api/platform/operations-summary', undefined, signal);
  }

  getMetrics(signal?: AbortSignal): Promise<PlatformMetricDefinition[]> {
    return this.request('/api/platform/metrics', undefined, signal);
  }
}

/** Local-development stand-in (VITE_USE_MOCKS=true) over a synthetic, clearly fictional dataset. The real backend never hard-codes a figure. */
export class MockPlatformOperationsGateway implements PlatformOperationsGateway {
  async getOperations(): Promise<PlatformOperationsResponse> {
    return structuredClone(MOCK_PLATFORM_OPERATIONS);
  }

  async getMetrics(): Promise<PlatformMetricDefinition[]> {
    return structuredClone(MOCK_PLATFORM_METRICS);
  }
}

export const platformOperationsGateway: PlatformOperationsGateway = useMocks
  ? new MockPlatformOperationsGateway()
  : new HttpPlatformOperationsGateway();
